import { useRef, useState } from 'react'
import { Col, Row } from 'antd'

import { PaginationMeta } from '../../../../models/pagination'
import { V3Transaction } from '../../../../models/v3-transaction'

import { useAppState } from '../../../../state'
import TransactionsTable from './TransactionsTable'
import TransactionPanel from '../panel/TransactionPanel'
import TransactionsCollapse from '../collapse/TransactionsCollapse'
import { MeterValuesModal } from './MeterValuesModal'

interface props {
  loading: boolean
  totalCountLoading: boolean
  isBulkSettleVisible: boolean
  selectedRowKeys: number[]
  bulkSettleLoading: boolean
  transactions: V3Transaction[]
  pagination: PaginationMeta
  onPageChange: (page: number) => void
  reloadTransactions: () => void
  onRowSelect: (record: V3Transaction, selected: boolean) => void
  onSelectAllOnPage: (selected: boolean) => void
  onBulkSettle: () => void
}

const TransactionsTableView: React.FC<props> = ({
  loading,
  totalCountLoading,
  isBulkSettleVisible,
  selectedRowKeys,
  bulkSettleLoading,
  transactions,
  pagination,
  onPageChange,
  reloadTransactions,
  onRowSelect,
  onSelectAllOnPage,
  onBulkSettle,
}) => {
  const { IsDesktop } = useAppState()

  const isDesktop = IsDesktop()

  const [showPanel, setShowPanel] = useState(false)
  const [position, setPosition] = useState(71)
  const [rowIndex, setRowIndex] = useState(1)
  const [meterValuesTransaction, setMeterValuesTransaction] = useState<V3Transaction | null>(null)
  const selectedTransactionIdRef = useRef<number | null>(null)

  const selectedTransaction = transactions.find((transaction) => transaction.id === selectedTransactionIdRef.current)

  const handleSelectedTransactionIdRef = (record: V3Transaction) => {
    selectedTransactionIdRef.current = record.id
  }

  const handlePanel = () => setShowPanel(!showPanel)

  const canShowPanel = showPanel && selectedTransaction ? true : false

  const handleRowIndex = (rowIndex?: number) => {
    if (!rowIndex) {
      return setRowIndex(0)
    }

    if (rowIndex) {
      return setRowIndex(rowIndex + 1)
    }
  }

  const panelOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    width: 420,
    maxWidth: '100%',
    height: '100vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: 'white',
    boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
    zIndex: 1000,
    boxSizing: 'border-box',
    paddingBottom: 12,
  }

  return (
    <>
      <MeterValuesModal
        transaction={meterValuesTransaction}
        visible={meterValuesTransaction != null}
        onClose={() => setMeterValuesTransaction(null)}
      />
      {isDesktop ? (
        <Row style={{ display: 'flex' }} gutter={[2, 80]}>
          {/* Table always full width; panel overlays on top when open */}
          <Col xs={24} lg={24} xl={24} xxl={24}>
            <TransactionsTable
              loading={loading}
              totalCountLoading={totalCountLoading}
              isBulkSettleVisible={isBulkSettleVisible}
              selectedRowKeys={selectedRowKeys}
              bulkSettleLoading={bulkSettleLoading}
              selectedTransaction={selectedTransaction}
              transactions={transactions}
              pagination={pagination}
              onPageChange={onPageChange}
              onSelectedTransaction={(record, index) => {
                handleRowIndex(index)
                handleSelectedTransactionIdRef(record)
              }}
              showPanel={canShowPanel}
              onShowPanel={handlePanel}
              onPosition={(record) => setPosition(record)}
              onChargingDurationClick={(record) => setMeterValuesTransaction(record)}
              onRowSelect={onRowSelect}
              onSelectAllOnPage={onSelectAllOnPage}
              onBulkSettle={onBulkSettle}
            />
          </Col>

          {/* Transaction panel overlay: full viewport height, own scroll */}
          {selectedTransaction && showPanel ? (
            <div style={panelOverlayStyle}>
              <TransactionPanel
                loading={loading}
                selectedTransaction={selectedTransaction}
                onShowPanel={handlePanel}
                position={position}
                rowIndex={rowIndex}
                totalTransactions={transactions.length}
                reloadTransactions={reloadTransactions}
              />
            </div>
          ) : null}
        </Row>
      ) : (
        <TransactionsCollapse
          loading={loading}
          totalCountLoading={totalCountLoading}
          transactions={transactions}
          pagination={pagination}
          onPaginationChange={onPageChange}
        />
      )}
    </>
  )
}

export default TransactionsTableView

